/**
 * TravellersSelector — popover with +/- steppers for Adults, Children, Infants.
 * Defaults to "1 Traveller". Reports counts up via onChange.
 *
 * @param {Object} props
 * @param {{adults:number, children:number, infants:number}} props.value
 * @param {(next:{adults:number, children:number, infants:number}) => void} props.onChange
 */
import React, { useEffect, useRef, useState } from "react";
import "./travellers-selector.css";

const ROWS = [
  { key: "adults", label: "Adults", hint: "12+ years", min: 1 },
  { key: "children", label: "Children", hint: "2–11 years", min: 0 },
  { key: "infants", label: "Infants", hint: "Under 2 years", min: 0 },
];

export default function TravellersSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const total = value.adults + value.children + value.infants;

  const step = (key, delta, min) => {
    const next = Math.max(min, value[key] + delta);
    // Infants can't outnumber adults (lap infants).
    if (key === "infants" && next > value.adults) return;
    onChange({ ...value, [key]: next });
  };

  return (
    <div className="trv" ref={wrapRef}>
      <label className="trv__label">Travellers</label>
      <button
        type="button"
        className={`trv__trigger ${open ? "trv__trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <i className="fas fa-user-friends trv__icon" aria-hidden="true" />
        <span className="trv__value">
          {total} Traveller{total === 1 ? "" : "s"}
        </span>
        <i className={`fas fa-chevron-down trv__chevron ${open ? "trv__chevron--up" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="trv__pop">
          {ROWS.map((row) => (
            <div className="trv__row" key={row.key}>
              <div className="trv__row-text">
                <span className="trv__row-label">{row.label}</span>
                <span className="trv__row-hint">{row.hint}</span>
              </div>
              <div className="trv__stepper">
                <button
                  type="button"
                  className="trv__step"
                  onClick={() => step(row.key, -1, row.min)}
                  disabled={value[row.key] <= row.min}
                  aria-label={`Decrease ${row.label}`}
                >
                  <i className="fas fa-minus" aria-hidden="true" />
                </button>
                <span className="trv__count">{value[row.key]}</span>
                <button
                  type="button"
                  className="trv__step"
                  onClick={() => step(row.key, 1, row.min)}
                  aria-label={`Increase ${row.label}`}
                >
                  <i className="fas fa-plus" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="trv__done" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
